import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthProvider';
import { FaWhatsapp, FaPowerOff, FaPlay, FaSpinner, FaQrcode } from 'react-icons/fa';
import QRCode from 'react-qr-code';

// ===================================================================================
// A. Status Indicator Component
// Provides a clear, colored, and glowing indicator for the service status.
// ===================================================================================
const StatusIndicator = ({ status }) => {
    const statusConfig = {
        disconnected: { text: 'Disconnected', color: 'bg-red-500', shadow: 'shadow-red-500/50' },
        connecting: { text: 'Connecting', color: 'bg-yellow-500', shadow: 'shadow-yellow-500/50' },
        connected: { text: 'Connected', color: 'bg-green-500', shadow: 'shadow-green-500/50' },
    };
    const currentStatus = statusConfig[status] || statusConfig.disconnected;

    return (
        <div className="flex items-center gap-3">
            <div className={`relative w-3 h-3 rounded-full ${currentStatus.color}`}>
                <div className={`absolute -inset-1 w-full h-full rounded-full ${currentStatus.color} opacity-75 animate-ping`}></div>
            </div>
            <span className={`font-semibold text-lg capitalize text-white`}>{currentStatus.text}</span>
        </div>
    );
};


// ===================================================================================
// B. Main WhatsApp Connector Component
// ===================================================================================
export function WhatsappConnector() {
    const { auth, socket } = useAuth();
    const [status, setStatus] = useState('disconnected');
    const [statusMessage, setStatusMessage] = useState('Service is not running.');
    const [qrCode, setQrCode] = useState('');
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const logsEndRef = useRef(null);

    const scrollToBottom = () => logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    useEffect(scrollToBottom, [logs]);

    useEffect(() => {
        if (!socket) return;
        
        const addLog = (message) => setLogs(prev => [...prev.slice(-100), `[${new Date().toLocaleTimeString()}] ${message}`]);
        const handleStatusUpdate = ({ status, message }) => { setStatus(status); setStatusMessage(message); if (status !== 'connecting') setQrCode(''); };
        const handleQrUpdate = ({ qr }) => setQrCode(qr);
        const handleLogUpdate = ({ message }) => addLog(message);

        socket.on('whatsapp-status', handleStatusUpdate);
        socket.on('whatsapp-qr', handleQrUpdate);
        socket.on('whatsapp-log', handleLogUpdate);

        fetch('/api/superadmin/whatsapp/status', { headers: { 'Authorization': `Bearer ${auth.token}` }})
            .then(res => res.json())
            .then(data => { setStatus(data.status); setStatusMessage(data.message); });

        return () => {
            socket.off('whatsapp-status', handleStatusUpdate);
            socket.off('whatsapp-qr', handleQrUpdate);
            socket.off('whatsapp-log', handleLogUpdate);
        };
    }, [socket, auth.token]);

    const handleAction = async (action) => {
        setIsLoading(true);
        setLogs([]); // Clear logs on new action
        try {
            await fetch(`/api/superadmin/whatsapp/${action}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${auth.token}` },
            });
        } catch (error) {
            console.error(`Failed to ${action} bot`, error);
        } finally {
            setIsLoading(false);
        }
    };

    const isStartDisabled = isLoading || status === 'connected' || status === 'connecting';
    const isStopDisabled = isLoading || status === 'disconnected';

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            
            {/* Left Panel: Controls and Status */}
            <div className="lg:col-span-2 bg-gray-800/50 border border-gray-700 rounded-lg p-6 flex flex-col">
                <StatusIndicator status={status} />
                <p className="text-gray-400 mt-2 mb-6 min-h-[40px]">{statusMessage}</p>

                <div className="flex-grow flex items-center justify-center bg-gray-900/50 border border-gray-700 rounded-lg p-4">
                    {/* QR Code Display */}
                    {status === 'connecting' && qrCode && (
                        <div className="text-center">
                            <h3 className="font-bold mb-2 text-white">Scan with WhatsApp</h3>
                            <div className="bg-white p-4 rounded-lg shadow-inner">
                                <QRCode value={qrCode} size={192} bgColor="#FFFFFF" fgColor="#000000" />
                            </div>
                        </div>
                    )}
                    {/* Waiting for QR */}
                    {status === 'connecting' && !qrCode && (
                        <div className="text-center text-gray-400 space-y-3">
                            <FaSpinner className="animate-spin text-4xl mx-auto" />
                            <p>Waiting for QR Code...</p>
                        </div>
                    )}
                    {/* Connected State */}
                    {status === 'connected' && (
                        <div className="text-center text-green-400 space-y-3">
                           <FaWhatsapp size={48} />
                           <p className="font-semibold">Service is connected and running.</p>
                        </div>
                    )}
                    {/* Disconnected State */}
                    {status === 'disconnected' && !isLoading && (
                        <div className="text-center text-gray-500 space-y-3">
                            <FaPowerOff size={48} />
                            <p>Service is stopped. Press "Start" to begin.</p>
                        </div>
                    )}
                </div>

                <div className="flex gap-4 mt-6">
                    <button onClick={() => handleAction('start')} disabled={isStartDisabled}
                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all">
                        <FaPlay/> Start Service
                    </button>
                    <button onClick={() => handleAction('stop')} disabled={isStopDisabled}
                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all">
                        <FaPowerOff/> Stop Service
                    </button>
                </div>
            </div>

            {/* Right Panel: Live Logs Terminal */}
            <div className="lg:col-span-3 bg-gray-900 border border-gray-700 rounded-lg flex flex-col h-[60vh] lg:h-auto">
                <div className="p-4 border-b border-gray-700">
                    <h3 className="font-semibold text-white">Live Logs</h3>
                </div>
                <div className="flex-1 p-4 font-mono text-xs text-gray-300 overflow-y-auto">
                    {logs.length > 0 
                        ? logs.map((log, i) => <p key={i} className="whitespace-pre-wrap">{log}</p>) 
                        : <p className="text-gray-500">Logs will appear here when the service is started...</p>
                    }
                    <div ref={logsEndRef} />
                </div>
            </div>
        </div>
    );
}